import { MasterpassAccountAccessResult, MasterpassResponse, MasterpassResult } from 'Models/MasterpassSdkModels';
import { MasterPassAccountStatusEnum, MasterpassResponseCodeEnum } from './Enums';

/**
 * Masterpass SDK v1.0.7 cevabı okuma yardımcıları.
 *
 * Eski MFS SDK'sında sonuç kodu her zaman düz `res.responseCode` alanındaydı.
 * Yeni SDK'da kod duruma göre iki farklı yerde bulunur:
 *
 *  - HTTP 202 (OTP/3D adımı) → hem `result.responseCode` hem kök `responseCode` dolu
 *  - HTTP 200 (ödeme/verify) → SADECE `result.responseCode` dolu, kök alan boş
 *  - HTTP hata               → SADECE kök `responseCode` dolu (`exception.code`)
 *
 * Bu yüzden koda doğrudan erişmek yerine daima buradaki fonksiyonlar kullanılmalı;
 * aksi halde başarılı ödemeler "kod yok" diye hata sayılır.
 */

/** Cevaptan işlem sonuç kodunu, hangi alanda olursa olsun okur. */
export const getMasterpassResponseCode = (response?: MasterpassResponse<any>): string =>
  response?.result?.responseCode ?? response?.responseCode ?? '';

/**
 * Kullanıcıya gösterilecek hata/bilgi metnini okur.
 *
 * Sıra: işlem açıklaması → SDK'nın türettiği açıklama → banka/VPOS hata metni →
 * exception mesajı → servisin kök mesajı.
 *
 * Banka hatası (`vposErrorMessage`) exception mesajından önce gelir: ödeme
 * reddedildiğinde kullanıcıyı ilgilendiren asıl neden odur, `exception.message`
 * ise genellikle daha genel bir metindir.
 */
export const getMasterpassResponseDescription = (response?: MasterpassResponse<any>): string =>
  response?.result?.description ??
  response?.description ??
  response?.exception?.vposErrorMessage ??
  response?.exception?.message ??
  response?.message ??
  '';

/**
 * Hata ayıklama/destek için tek satırlık teknik özet üretir.
 *
 * Masterpass'e açılan kayıtlarda korelasyon kimliği ve banka hata kodu isteniyor;
 * bu değerler kullanıcıya gösterilmez ama loglara mutlaka düşmelidir.
 */
export const getMasterpassErrorTrace = (response?: MasterpassResponse<any>): string =>
  [
    `code=${getMasterpassResponseCode(response) || '-'}`,
    `http=${response?.statusCode ?? '-'}`,
    `correlationId=${response?.correlationId ?? '-'}`,
    `requestId=${response?.requestId ?? '-'}`,
    `vpos=${response?.exception?.vposErrorCode ?? '-'}`,
    `rrn=${response?.exception?.retrievalReferenceNumber ?? '-'}`,
  ].join(' ');

/**
 * İşlemin başarılı olup olmadığını söyler.
 *
 * ⚠️ Eski kodda `res.responseCode == '0000' || res.responseCode == ''` kontrolü
 * yapılıyordu; boş string bazı akışlarda başarı anlamına geliyordu. Aynı tolerans
 * burada korunmuştur.
 */
export const isMasterpassSuccess = (response?: MasterpassResponse<any>): boolean => {
  const code = getMasterpassResponseCode(response);
  // ⚠️ `00` DA BAŞARIDIR. Masterpass bazı uçlarda dört haneli `0000` yerine
  // klasik ISO-8583 onay kodu `00` döndürüyor (sahada `recurringOrderDelete`:
  // HTTP 200, code=00, message="OK", exception=null). Eskiden bu hata sayılıyor,
  // silme gerçekte yapılmasına rağmen kullanıcıya hata gösteriliyor ve Belbim
  // kaydı güncellenmiyordu.
  //
  // Boş kod da başarıdır: hesap servisi uçları 200 cevabına `responseCode`
  // koymuyor (kart listeleme ve kart silme buna dayanıyor).
  return (
    code === MasterpassResponseCodeEnum.SUCCESS ||
    code === MasterpassResponseCodeEnum.SUCCESS_SHORT ||
    code === ''
  );
};

/** İşlemin SMS OTP adımı isteyip istemediğini söyler (`5001` veya `5008`). */
export const isMasterpassOtpRequired = (response?: MasterpassResponse<any>): boolean => {
  const code = getMasterpassResponseCode(response);
  return code === MasterpassResponseCodeEnum.OTP_REQUIRED || code === MasterpassResponseCodeEnum.OTP_REQUIRED_ALT;
};

/**
 * KART KAYDINDA doğrulama adımı gerekli mi.
 *
 * ⚠️ `5001`/`5008`'e ek olarak `5010` da OTP adımıdır — ama yalnızca kart
 * kaydında. Ödemede aynı kod banka 3D sayfasına yönlendirme demektir, o yüzden
 * ödeme dalında bu fonksiyon KULLANILMAZ.
 *
 * Kart kaydında servisin döndürdüğü cevap şöyle:
 *
 *   statusCode 202, responseCode "5010",
 *   description "Telefonunuza gelen tek kullanımlık şifreyi girerek işleminizi tamamlayınız",
 *   url3d ".../user-authorization/mp-3d-acs-form?token=..."
 *
 * Yani adım banka 3D'si değil, Masterpass'in kendi OTP doğrulaması; `url3d` de
 * bunun için sunduğu HAZIR SAYFA. Kendi OTP dialog'umuzu kullanabiliyoruz çünkü
 * SDK, kod ne olursa olsun her `202` cevabında `FlowDirectable.uri`'yi
 * `content-location` header'ından dolduruyor:
 *
 *   202==i ? (v(s.headers, a.result), a.token = h.token, a.url3d = h.url3d, ...)
 *
 * Dolayısıyla `verifyService.verifyOtp` bu adımda da doğru adrese gidiyor ve
 * kullanıcıyı harici sayfaya çıkarmaya gerek kalmıyor (uygulamada persist
 * olmadığı için tam sayfa yönlendirme akış durumunu kaybettirirdi).
 */
export const isMasterpassCardRegistrationOtpRequired = (response?: MasterpassResponse<any>): boolean =>
  isMasterpassOtpRequired(response) ||
  getMasterpassResponseCode(response) === MasterpassResponseCodeEnum.THREE_D_REDIRECT;

/**
 * Destek/hata takibi için Masterpass korelasyon kimliği.
 * Masterpass'e açılacak kayıtlarda bu değer isteniyor, loglara yazılmalı.
 */
export const getMasterpassCorrelationId = (response?: MasterpassResponse<any>): string =>
  response?.correlationId ?? (response?.result as MasterpassResult | undefined)?.correlationId ?? '';

/** `accountAccess` cevabındaki `accountState` değerinin "hesap kullanılabilir" hali. */
const ACCOUNT_STATE_ACTIVE = 'Activated';

/**
 * Masterpass hesap durumunu `accountAccess` cevabından türetir.
 *
 * Eski SDK'da bu bilgi `MFS.checkMasterPass` metodunun `accountStatus` alanından
 * geliyordu; yeni SDK'da o metot yok, bilgi hesap erişimi cevabına dağılmış durumda.
 * Eşleme canlı bir cevapla doğrulanmıştır:
 *
 *   `result.accountState`                      → hesap aktif mi   (örn. `"Activated"`)
 *   `result.accountInformation.isAccountLinked` → üye işyerine bağlı mı
 *   `result.cards`                              → kayıtlı kart var mı
 *
 * Dönüş değerleri `MasterPassAccountStatusEnum` ile uyumludur; boş string
 * "özel bir durum yok, akış normal ilerlesin" anlamına gelir.
 */
export const deriveMasterpassAccountStatus = (response?: MasterpassResponse<MasterpassAccountAccessResult>): string => {
  // Çağrı başarısızsa (yetkisiz, hesap yok) kullanıcı Masterpass'e kayıtlı değil sayılır.
  if (!isMasterpassSuccess(response)) {
    return MasterPassAccountStatusEnum.NO_ACCOUNT;
  }

  const result = response?.result;

  // Hesap var ama aktif değil (bloke/askıda): kart ekleme ve ödeme akışları
  // açılmamalı, kullanıcı bilgilendirilmeli.
  if (result?.accountState && result.accountState !== ACCOUNT_STATE_ACTIVE) {
    return MasterPassAccountStatusEnum.BLOCKED_ACCOUNT;
  }

  // Hesap aktif ama bu üye işyerine bağlanmamış: önce bağlama (linkToMerchant) akışı.
  if (result?.accountInformation?.isAccountLinked === false) {
    return MasterPassAccountStatusEnum.NO_LINKED;
  }

  if (!result?.cards?.length) {
    return MasterPassAccountStatusEnum.YES_ACCOUNT_NO_CARD;
  }

  return '';
};
